import React, { useEffect, useRef, useState } from 'react';
import { CheckCircle2, XCircle, Loader2, Zap, Sparkles } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useCreateCheckoutSession } from '@/hooks/usePayment';
import { Modal } from '@/components/ui/Modal';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { SocialLogin } from '@/features/auth/components/SocialLogin';
import { EmailLogin } from '@/features/auth/components/EmailLogin';
import { analyticsService } from '@/services/AnalyticsService';

// ---------------------------------------------------------------------------
// DB product row (authoritative — price, credits come from here, never hardcoded)
// ---------------------------------------------------------------------------
interface DbProduct {
  id: string;
  name: string;
  price_ils: number;
  credits_to_grant: number;
}

// ---------------------------------------------------------------------------
// UX enrichment map — purely presentation, keyed by product id.
// displayName: what users see on the card.
// DB product.name: what goes to Make.com/Grow (never rendered in UI).
// ---------------------------------------------------------------------------
const PRODUCT_UX: Record<string, { displayName: string; badge: string | null; description: string }> = {
  ai_credits_test: {
    displayName: 'בדיקה',
    badge: '🧪',
    description: '₪0.44 · 10 קרדיטים · לצורכי פיתוח בלבד',
  },
  ai_credits_500: {
    displayName: 'רוצה לדעת עליי',
    badge: null,
    description: 'AI לשיחת היכרות ללא התחייבות',
  },
  ai_credits_1500: {
    displayName: 'רוצה להעמיק עוד',
    badge: 'פופולרי',
    description: 'הבחירה הפופולרית · 18% חיסכון במחיר',
  },
  ai_credits_3300: {
    displayName: 'רוצה לשאול הרבה יותר',
    badge: 'הטוב ביותר',
    description: 'לחוקרים רציניים · 25% חיסכון במחיר',
  },
};

// ---------------------------------------------------------------------------
// Query hook — fetch credit products from public.products
// ---------------------------------------------------------------------------
function useProducts() {
  return useQuery<DbProduct[]>({
    queryKey: ['products', 'ai_credits'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('products')
        .select('id, name, price_ils, credits_to_grant')
        .or('id.like.ai_credits_%,id.eq.premium_monthly,id.eq.premium_monthly_test')
        .order('price_ils', { ascending: true });
      if (error) throw error;
      return data as DbProduct[];
    },
    staleTime: 1000 * 60 * 5, // 5 min — product catalog doesn't change frequently
  });
}

type PaymentStatus =
  | 'INIT'
  | 'AUTH_REQUIRED'
  | 'EMAIL_LOGIN'
  | 'PRODUCT_SELECTION'
  | 'LOADING_URL'
  | 'WAITING_FOR_PAYMENT'
  | 'SUCCESS'
  | 'FAILED';

interface CheckoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  title?: string;
}

export const CheckoutModal: React.FC<CheckoutModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  title = 'רכישת קרדיטים',
}) => {
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>('INIT');
  const [_checkoutUrl, setCheckoutUrl] = useState<string | null>(null);
  const [paymentRequestId, setPaymentRequestId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Popup & timing lifecycle refs
  const popupRef = useRef<Window | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const gracePeriodRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const paymentStatusRef = useRef<PaymentStatus>('INIT');
  const modalOpenedAtRef = useRef<number>(0);
  const popupOpenedAtRef = useRef<number>(0);
  const selectedProductIdRef = useRef<string | null>(null);

  // Keep ref in sync so polling interval doesn't read stale state
  useEffect(() => { paymentStatusRef.current = paymentStatus; }, [paymentStatus]);

  const stopPopupPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (gracePeriodRef.current) { clearTimeout(gracePeriodRef.current); gracePeriodRef.current = null; }
  };

  const { mutate: createSession } = useCreateCheckoutSession();
  const { user, isPremium } = useAuth();
  const { data: products, isLoading: productsLoading, isError: productsError } = useProducts();

  // Reset state when modal closes — also kill any running popup poll
  useEffect(() => {
    if (!isOpen) {
      if (paymentStatus !== 'INIT' && paymentStatus !== 'SUCCESS') {
        const durationSeconds = modalOpenedAtRef.current
          ? Math.round((Date.now() - modalOpenedAtRef.current) / 1000)
          : 0;
        analyticsService.track('checkout_canceled', {
          last_status: paymentStatus,
          duration_seconds: durationSeconds,
          selected_product_id: selectedProductIdRef.current || undefined
        });
      }
      stopPopupPolling();
      popupRef.current?.close();
      popupRef.current = null;
      modalOpenedAtRef.current = 0;
      popupOpenedAtRef.current = 0;
      selectedProductIdRef.current = null;
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setPaymentStatus('INIT');
      setCheckoutUrl(null);
      setPaymentRequestId(null);
      setErrorMessage(null);
    } else if (isOpen && paymentStatus === 'INIT') {
      modalOpenedAtRef.current = Date.now();
      const initialStatus: PaymentStatus = user ? 'PRODUCT_SELECTION' : 'AUTH_REQUIRED';
      analyticsService.track('checkout_modal_opened', {
        initial_status: initialStatus,
        is_authenticated: !!user,
        is_premium: isPremium
      });
      analyticsService.track('checkout_start');
      setPaymentStatus(initialStatus);
    }
  }, [isOpen, paymentStatus, user, isPremium]);

  // When user logs in during AUTH_REQUIRED / EMAIL_LOGIN → go to product selection
  useEffect(() => {
    if (user && (paymentStatus === 'AUTH_REQUIRED' || paymentStatus === 'EMAIL_LOGIN')) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setPaymentStatus('PRODUCT_SELECTION');
    }
  }, [user, paymentStatus]);

  // Called when user clicks a product card
  const handleSelectProduct = (productId: string) => {
    selectedProductIdRef.current = productId;
    const selectedProduct = products?.find(p => p.id === productId);

    analyticsService.track('payment_plan_selected', {
      product_id: productId,
      price_ils: selectedProduct?.price_ils,
      credits: selectedProduct?.credits_to_grant
    });

    setPaymentStatus('LOADING_URL');
    createSession(
      { productId },
      {
        onSuccess: async (data) => {
          // Mock bypass: payment already fulfilled server-side — skip payment window
          if (data.url.includes('mock=true')) {
            await supabase.auth.refreshSession();
            setPaymentStatus('SUCCESS');
            if (onSuccess) onSuccess();
            return;
          }
          // Open Grow payment in popup (Grow blocks iframing via frame-ancestors CSP)
          setPaymentRequestId(data.payment_request_id);
          setPaymentStatus('WAITING_FOR_PAYMENT');
          popupOpenedAtRef.current = Date.now();

          // Store reference — we close it programmatically on Realtime signal
          const popup = window.open(data.url, 'grow_checkout', 'width=500,height=700');
          popupRef.current = popup;

          // Check whether the popup was blocked by browser pop-up blocker
          if (!popup || popup.closed || typeof popup.closed === 'undefined') {
            analyticsService.track('checkout_popup_blocked', {
              payment_request_id: data.payment_request_id,
              product_id: productId
            });
            setErrorMessage('דפדפן האינטרנט חסם את חלון התשלום. נא לאפשר חלונות קופצים בדפדפן ולנסות שוב.');
            setPaymentStatus('FAILED');
            return;
          }

          analyticsService.track('checkout_popup_opened', {
            payment_request_id: data.payment_request_id,
            product_id: productId
          });

          // Poll: detect if user closes the popup before payment completes
          stopPopupPolling();
          pollRef.current = setInterval(() => {
            if (popupRef.current?.closed) {
              stopPopupPolling();
              const elapsedSeconds = popupOpenedAtRef.current
                ? Math.round((Date.now() - popupOpenedAtRef.current) / 1000)
                : 0;

              analyticsService.track('checkout_popup_closed_manually', {
                payment_request_id: data.payment_request_id,
                product_id: productId,
                elapsed_seconds: elapsedSeconds
              });

              // 6-second grace period — give Realtime time to fire even after popup closes
              gracePeriodRef.current = setTimeout(() => {
                if (paymentStatusRef.current === 'WAITING_FOR_PAYMENT') {
                  setErrorMessage('החלון נסגר לפני קבלת אישור. אם שילמת, הקרדיטים יתווספו בקרוב.');
                  setPaymentStatus('FAILED');
                }
              }, 6000);
            }
          }, 1000);
        },
        onError: (error) => {
          analyticsService.track('checkout_error_returned', {
            error_code: error.message,
            product_id: productId,
            stage: 'create_session'
          });

          if (error.message === 'rate_limited') {
            setErrorMessage('יותר מדי ניסיונות תשלום. אנא המתן 15 דקות לפני ניסיון נוסף.');
          } else {
            setErrorMessage(error.message || 'שגיאה באתחול תשלום.');
          }
          setPaymentStatus('FAILED');
        },
      },
    );
  };

  // Realtime listener — detects SUCCESS/FAILED on the payment_request row
  useEffect(() => {
    if (!paymentRequestId || paymentStatus === 'SUCCESS' || paymentStatus === 'FAILED') return;

    const channel = supabase
      .channel(`payment_status_${paymentRequestId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'payment_requests',
          filter: `id=eq.${paymentRequestId}`,
        },
        async (payload) => {
          const newStatus = payload.new.status;
          if (newStatus === 'SUCCESS') {
            analyticsService.track('checkout_complete', { payment_request_id: paymentRequestId });
            stopPopupPolling();
            popupRef.current?.close(); // close Grow popup on confirmed success
            popupRef.current = null;
            await supabase.auth.refreshSession();
            setPaymentStatus('SUCCESS');
            if (onSuccess) onSuccess();
          } else if (newStatus === 'FAILED') {
            analyticsService.track('checkout_error_returned', {
              error_code: 'grow_payment_rejected',
              payment_request_id: paymentRequestId,
              stage: 'payment_completion'
            });
            stopPopupPolling();
            popupRef.current?.close(); // close Grow popup on confirmed failure
            popupRef.current = null;
            setErrorMessage('התשלום נדחה או נכשל. נא לנסות שוב.');
            setPaymentStatus('FAILED');
          }
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
      stopPopupPolling();
    };
  }, [paymentRequestId, paymentStatus, onSuccess]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <span className="bg-gradient-to-r from-gold-200 via-gold-400 to-gold-300 bg-clip-text text-transparent drop-shadow-[0_2px_8px_rgba(245,158,11,0.2)]">
          {title}
        </span>
      }
      maxWidth="max-w-2xl"
    >
      <div className="relative flex flex-col items-center justify-center min-h-[380px] w-full" dir="rtl">
        {/* Stars stardust background pattern layer */}
        <div className="absolute inset-0 bg-stars opacity-[0.03] pointer-events-none select-none z-0" />

        {/* ── AUTH_REQUIRED ── */}
        {paymentStatus === 'AUTH_REQUIRED' && (
          <div className="w-full flex items-center justify-center animate-in fade-in zoom-in-95 duration-500 py-4">
            <SocialLogin onEmailLoginClick={() => setPaymentStatus('EMAIL_LOGIN')} />
          </div>
        )}

        {/* ── EMAIL_LOGIN ── */}
        {paymentStatus === 'EMAIL_LOGIN' && (
          <div className="w-full flex flex-col items-center justify-center animate-in fade-in zoom-in-95 duration-500 py-4 max-w-sm mx-auto">
            <EmailLogin />
            <button
              onClick={() => setPaymentStatus('AUTH_REQUIRED')}
              className="mt-6 text-sm text-mystic-400 hover:text-gold-400 transition-colors underline-offset-2 hover:underline"
            >
              חזרה לאפשרויות התחברות
            </button>
          </div>
        )}

        {/* ── PRODUCT_SELECTION ── */}
        {paymentStatus === 'PRODUCT_SELECTION' && (
          <div className="w-full space-y-4 animate-in fade-in zoom-in-95 duration-500">
            {/* Premium Dynamic Discount Notice (No Emojis) */}
            {isPremium ? (
              <div className="mx-auto w-fit px-4 py-1.5 rounded-xl border border-gold-500/25 bg-gradient-to-r from-gold-500/10 via-amber-500/15 to-yellow-600/10 text-gold-200 text-[11px] font-bold text-center shadow-[0_0_15px_rgba(234,179,8,0.05)] backdrop-blur-md animate-in fade-in slide-in-from-top-2 duration-300">
                הטבת פרימיום פעילה: 10% יותר קרדיטים בכל רכישה
              </div>
            ) : (
              <div className="mx-auto w-fit px-4 py-1.5 rounded-xl border border-purple-500/20 bg-gradient-to-r from-purple-500/10 via-purple-600/5 to-transparent text-purple-200 text-[11px] font-medium text-center backdrop-blur-sm">
                מנויי פרימיום מקבלים 10% יותר קרדיטים ברכישת חבילות.{" "}
                <button
                  onClick={() => {
                    const premiumProduct = products?.find(p => p.id === 'premium_monthly');
                    if (premiumProduct) handleSelectProduct(premiumProduct.id);
                  }}
                  className="text-gold-400 hover:text-gold-300 font-bold underline underline-offset-2 ml-1 transition-colors bg-transparent border-none cursor-pointer p-0"
                >
                  שדרוג לפרימיום
                </button>
              </div>
            )}

            {/* Products loading */}
            {productsLoading && (
              <div className="flex justify-center py-10">
                <Loader2 className="w-8 h-8 text-gold-400 animate-spin" />
              </div>
            )}

            {/* Products fetch error */}
            {productsError && (
              <p className="text-center text-red-400 text-sm py-6">
                שגיאה בטעינת החבילות. נסה לרענן את הדף.
              </p>
            )}

            {/* Product cards — data from DB, presentation from PRODUCT_UX */}
            {products && (() => {
              const tokenProducts = products.filter(p => p.id.startsWith('ai_credits_') && p.id !== 'ai_credits_test');
              const premiumProduct = products.find(p => p.id === 'premium_monthly');
              const testProduct = import.meta.env.DEV ? products.find(p => p.id === 'ai_credits_test') : undefined;
              const testPremiumProduct = import.meta.env.DEV ? products.find(p => p.id === 'premium_monthly_test') : undefined;

              return (
                <>
                  <div className="grid grid-cols-3 gap-3 pt-4">
                    {tokenProducts.map((product, i) => {
                      const ux = PRODUCT_UX[product.id] ?? { badge: null, description: '' };
                      const isMiddle = i === 1;
                      return (
                        <button
                          key={product.id}
                          onClick={() => handleSelectProduct(product.id)}
                          className={`
                            relative flex flex-col items-center text-center p-4 rounded-2xl border w-full
                            transition-all duration-300 hover:scale-[1.03] active:scale-[0.98] cursor-pointer z-10
                            ${isMiddle
                              ? 'bg-gradient-to-b from-gold-500/10 via-mystic-950/40 to-mystic-1000/60 border-gold-500/30 shadow-[0_0_30px_rgba(245,158,11,0.15)] backdrop-blur-xl'
                              : 'bg-mystic-1000/30 border-white/5 hover:border-gold-500/20 hover:bg-white/5 backdrop-blur-md'
                            }
                          `}
                        >
                          {/* Gold radial aura glow behind middle card */}
                          {isMiddle && (
                            <div className="absolute inset-0 bg-gold-500/5 blur-2xl rounded-2xl pointer-events-none -z-10" />
                          )}

                          {/* Premium badge on the card if premium (No Emojis) */}
                          {isPremium && (
                            <span className="absolute top-2.5 left-2.5 bg-gold-500/20 text-gold-300 text-[9px] font-bold px-1.5 py-0.5 rounded border border-gold-500/30 tracking-wider">
                              +10%
                            </span>
                          )}

                          {/* Badge */}
                          {ux.badge && (
                            <span className={`
                              absolute -top-2.5 right-1/2 translate-x-1/2
                              text-[10px] font-bold px-2.5 py-0.5 rounded-full whitespace-nowrap z-20
                              ${isMiddle
                                ? 'bg-gold-500 text-mystic-1100'
                                : 'bg-mystic-700 text-gold-400 border border-gold-500/30'
                              }
                            `}>
                              {ux.badge}
                            </span>
                          )}

                          {/* Icon */}
                          <Zap
                            size={20}
                            className={`mb-2 mt-1 ${isMiddle ? 'text-gold-400 drop-shadow-[0_0_8px_rgba(245,158,11,0.5)] animate-pulse' : 'text-mystic-400'}`}
                          />

                          {/* Credits — from DB (Upgraded dynamically if premium) */}
                          <div className="flex flex-col items-center mb-1">
                            {isPremium ? (
                              <>
                                <span className="text-[10px] text-mystic-400 font-semibold tracking-wide">
                                  {`${Math.round(product.credits_to_grant * 0.1).toLocaleString()} + ${product.credits_to_grant.toLocaleString()}`}
                                </span>
                                <span className={`text-2xl font-black leading-none ${isMiddle ? 'text-gold-300' : 'text-white'} drop-shadow-[0_2px_8px_rgba(245,158,11,0.2)]`}>
                                  {Math.round(product.credits_to_grant * 1.1).toLocaleString()}
                                </span>
                              </>
                            ) : (
                              <span className={`text-2xl font-black leading-none ${isMiddle ? 'text-gold-300' : 'text-white'}`}>
                                {product.credits_to_grant.toLocaleString()}
                              </span>
                            )}
                          </div>
                          <span className="text-[10px] text-mystic-400 mb-3">קרדיטים</span>

                          {/* Display name */}
                          <span className="text-xs font-semibold text-mystic-200 mb-1 leading-tight px-1">{ux.displayName}</span>

                          {/* Description */}
                          <span className="text-[10px] text-mystic-400 leading-tight mb-4">{ux.description}</span>

                          {/* Price Display */}
                          <div className="flex flex-col items-center mb-3">
                            <span className={`text-2xl font-black leading-none ${
                              isMiddle 
                                ? 'bg-gradient-to-r from-gold-200 via-gold-400 to-gold-300 bg-clip-text text-transparent drop-shadow-[0_2px_8px_rgba(245,158,11,0.25)]' 
                                : 'text-white drop-shadow-[0_2px_6px_rgba(255,255,255,0.1)]'
                            }`}>
                              ₪{product.price_ils}
                            </span>
                          </div>

                          {/* Purchase Button */}
                          <span
                            className={`
                            w-full py-2.5 rounded-xl text-xs font-black tracking-wider transition-all duration-300 mt-auto
                            ${isMiddle
                                ? 'bg-gradient-to-r from-gold-400 via-gold-500 to-gold-600 text-mystic-1100 hover:from-gold-300 hover:via-gold-400 hover:to-gold-500 shadow-[0_0_20px_rgba(245,158,11,0.3)] hover:shadow-[0_0_30px_rgba(245,158,11,0.5)]'
                                : 'bg-white/5 text-mystic-200 border border-white/10 hover:bg-white/10 hover:border-white/20 hover:text-white'
                              }
                          `}
                          >
                            לרכישה
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  {premiumProduct && (
                    <>
                      <hr className="border-white/10 my-6 w-full" />
                      <div className="relative w-full">
                        <div className="w-full relative flex items-center justify-between p-6 rounded-2xl border border-gold-500/30 bg-gradient-to-br from-[#12052b] via-[#1f0945] to-[#0c021f] shadow-[0_0_35px_rgba(245,158,11,0.15)] transition-all duration-300 hover:shadow-[0_0_45px_rgba(245,158,11,0.25)] hover:border-gold-400/50 backdrop-blur-xl overflow-hidden group">
                          
                          {/* Shine effect inside the card */}
                          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-[150%] skew-x-[-45deg] transition-transform duration-1000 group-hover:translate-x-[150%] pointer-events-none" />

                          <div className="text-right space-y-3 pl-4 relative z-10">
                            <h4 className="text-xl font-black bg-gradient-to-r from-gold-200 via-gold-400 to-gold-300 bg-clip-text text-transparent drop-shadow-sm">
                              גישה חופשית לכל המערכת
                            </h4>
                            <ul className="text-xs text-mystic-200 space-y-2 max-w-[260px]">
                              <li className="flex items-start gap-2">
                                <Sparkles size={12} className="text-gold-400 mt-0.5 animate-pulse flex-shrink-0" />
                                <span className="leading-tight">פתיחת סימולטור הוספת שמות</span>
                              </li>
                              <li className="flex items-start gap-2">
                                <Sparkles size={12} className="text-gold-400 mt-0.5 animate-pulse flex-shrink-0" />
                                <span className="leading-tight">כל עמודי התוכן במרכז המידע</span>
                              </li>
                              <li className="flex items-start gap-2">
                                <Sparkles size={12} className="text-gold-400 mt-0.5 animate-pulse flex-shrink-0" />
                                <span className="leading-tight"><strong className="text-gold-300 font-bold">660 קרדיטים</strong> כל חודש לשימוש לייעוץ AI</span>
                              </li>
                              <li className="flex items-start gap-2">
                                <Sparkles size={12} className="text-gold-400 mt-0.5 animate-pulse flex-shrink-0" />
                                <span className="leading-tight"><strong className="text-gold-300 font-bold">10% יותר קרדיטים</strong> ברכישת חבילות נוספות</span>
                              </li>
                            </ul>
                          </div>

                          <div className="flex flex-col items-center relative z-10 pl-2">
                            <span className="text-2xl font-black text-white mb-1 drop-shadow-md">
                              ₪{premiumProduct.price_ils} <span className="text-sm font-normal text-mystic-400">/ חודש</span>
                            </span>
                            <span className="text-[10px] text-gold-400/80 mb-3 font-medium tracking-wide">ללא התחייבות, ביטול בקליק</span>
                            <button
                              onClick={() => handleSelectProduct(premiumProduct.id)}
                              className="relative px-8 py-2.5 bg-gradient-to-r from-gold-400 via-gold-500 to-gold-600 text-mystic-1100 font-black rounded-full hover:from-gold-300 hover:via-gold-400 hover:to-gold-500 transition-all shadow-[0_0_20px_rgba(245,158,11,0.3)] hover:shadow-[0_0_30px_rgba(245,158,11,0.5)] active:scale-95 overflow-hidden group/btn cursor-pointer"
                            >
                              <span className="relative z-10">שדרוג לפרימיום</span>
                              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-[150%] skew-x-[-45deg] transition-transform duration-700 group-hover/btn:translate-x-[150%] pointer-events-none" />
                            </button>
                          </div>
                        </div>
                        
                      {/* BADGE (Outside the overflow-hidden container) */}
                      <span className="absolute -top-2.5 right-6 bg-gradient-to-r from-gold-300 to-gold-500 text-mystic-1100 text-[11px] font-bold px-3 py-0.5 rounded-full whitespace-nowrap shadow-[0_0_15px_rgba(212,175,55,0.4)] border border-gold-200/50 z-20 flex items-center gap-1.5 pointer-events-none">
                        <Sparkles size={10} className="text-mystic-900 animate-pulse flex-shrink-0" />
                        <span>מנוי פרימיום</span>
                      </span>
                    </div>
                  </>
                  )}

                  {(testProduct || testPremiumProduct) && import.meta.env.DEV && (
                    <div className="mt-4 flex justify-center gap-3">
                      {testProduct && (
                        <button
                          onClick={() => handleSelectProduct(testProduct.id)}
                          className="flex items-center gap-2 px-4 py-1.5 rounded-full border border-dashed border-mystic-600 bg-mystic-800/50 hover:bg-mystic-700/50 hover:border-mystic-500 transition-colors group"
                        >
                          <span className="text-xs text-mystic-400 group-hover:text-mystic-200">
                            🛠 טסט רגיל
                          </span>
                          <span className="text-xs font-mono text-mystic-500 group-hover:text-mystic-300">
                            ₪{testProduct.price_ils}
                          </span>
                        </button>
                      )}
                      
                      {testPremiumProduct && (
                        <button
                          onClick={() => handleSelectProduct(testPremiumProduct.id)}
                          className="flex items-center gap-2 px-4 py-1.5 rounded-full border border-dashed border-gold-600/40 bg-gold-900/20 hover:bg-gold-800/30 hover:border-gold-500/60 transition-colors group"
                        >
                          <span className="text-xs text-gold-400/80 group-hover:text-gold-300">
                            🛠 טסט פרימיום 
                          </span>
                          <span className="text-xs font-mono text-gold-500/60 group-hover:text-gold-400/80">
                            ₪{testPremiumProduct.price_ils}
                          </span>
                        </button>
                      )}
                    </div>
                  )}
                </>
              );
            })()}

            <p className="text-center text-[10px] text-mystic-500/80 leading-normal mt-3 px-4">
              המשך לתשלום מאובטח מעדכן את יתרת הקרדיטים שלך בהתאם ל
              <a
                href="/terms-of-use"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-gold-400 underline underline-offset-2 transition-colors mx-1"
              >
                תנאי השימוש
              </a>
              ול
              <a
                href="/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-gold-400 underline underline-offset-2 transition-colors mx-1"
              >
                מדיניות הפרטיות
              </a>
              .
            </p>
          </div>
        )}

        {/* ── LOADING_URL ── */}
        {paymentStatus === 'LOADING_URL' && (
          <div className="flex flex-col items-center justify-center space-y-6 animate-in fade-in duration-500">
            <Loader2 className="w-12 h-12 text-gold-400 animate-spin" />
            <p className="text-white/70 text-lg">מאתחל מערכת תשלום מאובטחת...</p>
          </div>
        )}

        {/* ── WAITING_FOR_PAYMENT (popup opened, listening via Realtime) ── */}
        {paymentStatus === 'WAITING_FOR_PAYMENT' && (
          <div className="flex flex-col items-center justify-center space-y-6 py-10 animate-in fade-in duration-500">
            <div className="relative">
              <div className="absolute inset-0 bg-gold-500 blur-2xl opacity-10 rounded-full" />
              <Loader2 className="w-16 h-16 text-gold-400 animate-spin relative z-10" />
            </div>
            <div className="text-center space-y-2">
              <p className="text-white text-lg font-semibold">ממתין לאישור תשלום...</p>
              <p className="text-mystic-400 text-sm max-w-xs leading-relaxed">
                השלם את התשלום בחלון שנפתח.
                <br />
                הדף יתעדכן אוטומטית לאחר האישור.
              </p>
            </div>
            <button
              onClick={() => setPaymentStatus('PRODUCT_SELECTION')}
              className="text-xs text-mystic-500 hover:text-mystic-300 transition-colors underline-offset-2 hover:underline mt-2"
            >
              ביטול וחזרה לבחירת מוצר
            </button>
          </div>
        )}

        {/* ── SUCCESS ── */}
        {paymentStatus === 'SUCCESS' && (
          <div className="flex flex-col items-center justify-center space-y-6 py-8 animate-in zoom-in-95 fade-in duration-500">
            <div className="relative">
              <div className="absolute inset-0 bg-green-500 blur-2xl opacity-20 rounded-full" />
              <CheckCircle2 className="w-24 h-24 text-green-400 relative z-10" />
            </div>
            <h3 className="text-3xl font-black text-white">התשלום בוצע בהצלחה!</h3>
            <p className="text-white/70 text-center max-w-sm text-base leading-relaxed">
              הקרדיטים נוספו לחשבון שלך.<br />
              <span className="text-mystic-400 text-sm">ייתכן שיידרשו כמה שניות להתעדכן.</span>
            </p>
            <button
              onClick={onClose}
              className="px-8 py-3 mt-4 text-white font-bold bg-green-500/20 border border-green-500/30 rounded-full hover:bg-green-500/30 transition-all shadow-[0_0_20px_rgba(34,197,94,0.2)]"
            >
              המשך
            </button>
          </div>
        )}

        {/* ── FAILED ── */}
        {paymentStatus === 'FAILED' && (
          <div className="flex flex-col items-center justify-center space-y-6 py-8 animate-in zoom-in-95 fade-in duration-500">
            <div className="relative">
              <div className="absolute inset-0 bg-red-500 blur-2xl opacity-20 rounded-full" />
              <XCircle className="w-24 h-24 text-red-400 relative z-10" />
            </div>
            <h3 className="text-3xl font-black text-white">התשלום נכשל</h3>
            <p className="text-white/70 text-center max-w-sm text-lg">
              {errorMessage || 'לא הצלחנו לעבד את התשלום שלך כרגע.'}
            </p>
            <div className="flex gap-3 mt-4">
              <button
                onClick={() => {
                  setPaymentStatus('PRODUCT_SELECTION');
                  setErrorMessage(null);
                }}
                className="px-8 py-3 text-white font-bold bg-white/10 border border-white/20 rounded-full hover:bg-white/20 transition-all"
              >
                לנסות שוב
              </button>
            </div>
          </div>
        )}

      </div>
    </Modal>
  );
};
